import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, ArrowLeft, Clock, Video } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { CustomerInput } from "@/shared/components/ui/customer-input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import { useToast } from "@/shared/hooks/use-toast";
import { useSettings } from "@/shared/contexts/SettingsContext";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useTeamMembers } from "@/shared/hooks/useTeamMembers";
import { useCustomers } from "@/shared/hooks/useCustomers";
import { useAuth } from "@/shared/contexts/AuthContext";
import { apiClient } from "@/shared/services/apiClient";
import { getShareableBaseUrl } from "@/utils/getShareableBaseUrl";
import type { ExtensionHcpPrefill } from "@/shared/hooks/useExtensionHcpPrefill";

interface ScheduleCallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Filled by Chrome extension (HCP customer + optional date/time from extension schedule flow) */
  prefillData?: ExtensionHcpPrefill;
}

export function ScheduleCallModal({ open, onOpenChange, prefillData }: ScheduleCallModalProps) {
  const { scheduledCallMessage, organizationName, replaceTokens, userTimezone } = useSettings();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { checkAccess } = useSubscriptionGuard();
  const { usage } = useSubscription();
  const { createCustomer } = useCustomers();
  const { user } = useAuth();

  // Get current time for default time selection
  const getCurrentTimeOption = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = Math.ceil(now.getMinutes() / 15) * 15; // Round to next 15-minute interval
    const adjustedHour = currentMinute >= 60 ? (currentHour + 1) % 24 : currentHour;
    const finalMinute = currentMinute >= 60 ? 0 : currentMinute;
    return `${adjustedHour.toString().padStart(2, "0")}:${finalMinute
      .toString()
      .padStart(2, "0")}`;
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    countryCode: "+1",
    email: "",
    message: "",
    shareOnEmail: false,
    assignTo: "",
    date: undefined as Date | undefined,
    time: getCurrentTimeOption(), // Default to current time
    timezone: userTimezone,
  });

  const { teamMembers, loading: loadingTeam } = useTeamMembers();
  const [isLoading, setIsLoading] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);

  useEffect(() => {
    if (prefillData && open) {
      setFormData((prev) => {
        let countryCode = prev.countryCode;
        let phoneNumber = prev.phoneNumber;
        if (prefillData.phoneNumber) {
          const digits = prefillData.phoneNumber.replace(/\D/g, "");
          if (digits.length >= 10) {
            countryCode = "+1";
            phoneNumber = digits.slice(-10);
          }
        }
        const emailTrim = (prefillData.email || "").trim();

        let date = prev.date;
        let time = prev.time;
        if (prefillData.scheduleDate && prefillData.scheduleTime) {
          const dp = prefillData.scheduleDate.split("-").map(Number);
          if (dp.length === 3 && dp[0] > 0 && dp[1] >= 1 && dp[1] <= 12 && dp[2] >= 1 && dp[2] <= 31) {
            date = new Date(dp[0], dp[1] - 1, dp[2]);
          }
          time = prefillData.scheduleTime;
        }

        return {
          ...prev,
          firstName: prefillData.firstName || prev.firstName,
          lastName: prefillData.lastName || prev.lastName,
          countryCode,
          phoneNumber,
          email: emailTrim || prev.email,
          shareOnEmail: emailTrim ? true : prev.shareOnEmail,
          date,
          time,
        };
      });
    }
  }, [prefillData, open]);

  // Generate message preview with tokens replaced
  const getMessagePreview = () => {
    const message = formData.message || scheduledCallMessage;
    const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

    // Get assigned user's name from team members
    const assignedUser = teamMembers.find(tm => tm.id === formData.assignTo);
    const currentUserName = assignedUser?.name || '';

    // Create a proper Date object for the scheduled time
    let scheduledDateTime: Date | undefined;
    if (formData.date && formData.time) {
      const [hours, minutes] = formData.time.split(":").map(Number);
      scheduledDateTime = new Date(formData.date);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
    }

    const tokenData = {
      name: customerFullName || undefined, // Keep placeholder visible if no name
      firstName: formData.firstName.trim() || undefined,
      lastName: formData.lastName.trim() || undefined,
      organization: organizationName,
      date: scheduledDateTime,
      time: scheduledDateTime,
      timeZone: formData.timezone,
      userName: currentUserName || undefined,
    };

    return replaceTokens(message, tokenData);
  };

  // Generate final message for sending (replaces tokens with values or empty string)
  const getFinalMessage = () => {
    const message = formData.message || scheduledCallMessage;
    const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

    // Get assigned user's name from team members
    const assignedUser = teamMembers.find(tm => tm.id === formData.assignTo);
    const currentUserName = assignedUser?.name || '';

    // Create a proper Date object for the scheduled time
    let scheduledDateTime: Date | undefined;
    if (formData.date && formData.time) {
      const [hours, minutes] = formData.time.split(":").map(Number);
      scheduledDateTime = new Date(formData.date);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
    }

    const tokenData = {
      name: customerFullName || '', // Replace with actual value or empty string
      firstName: formData.firstName.trim() || '',
      lastName: formData.lastName.trim() || '',
      organization: organizationName,
      date: scheduledDateTime,
      time: scheduledDateTime,
      timeZone: formData.timezone,
      userName: currentUserName || '',
    };

    return replaceTokens(message, tokenData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submissionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('[ScheduleCallModal] handleSubmit called:', {
      submissionId,
      isLoading,
      timestamp: new Date().toISOString()
    });

    // SUBSCRIPTION GUARD: Check subscription access before proceeding
    if (!checkAccess('Schedule Call')) {
      console.log('[ScheduleCallModal] Access check failed:', submissionId);
      return; // checkAccess handles toast and navigation
    }

    // CHECK CALL LIMIT: Verify user hasn't exceeded free trial call limit
    // Only enforce limit for free trial users (NOT pro trial or subscribed users)
    // TODO: Re-enable after local testing
    // if (!usage.subscribed && !usage.is_pro_trial) {
    //   if (usage.calls_limit !== null && usage.calls_limit !== undefined) {
    //     if ((usage.calls_count ?? 0) >= usage.calls_limit) {
    //       toast({
    //         title: "Call Limit Reached",
    //         description: `You've reached your ${usage.calls_limit} call limit. Upgrade to continue scheduling calls.`,
    //         variant: "destructive"
    //       });
    //       return;
    //     }
    //   }
    // }

    // Validation
    if (
      !formData.firstName.trim() ||
      !formData.phoneNumber.trim() ||
      !formData.date ||
      !formData.time
    ) {
      toast({
        title: t("notifications.error"),
        description: "Please fill in all required fields (first name, phone, date, time)",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number - only allow digits and optional + at start
    const phoneRegex = /^\+?\d+$/;
    if (!phoneRegex.test(formData.phoneNumber.trim())) {
      toast({
        title: "Invalid Phone Number",
        description: "Phone number can only contain digits and an optional + at the beginning",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const customerName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber.trim()}`;

      // Save customer data to end_customers table using useCustomers hook
      const customer = await createCustomer({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone_number: fullPhoneNumber,
        email: formData.shareOnEmail ? formData.email.trim() || null : null,
        project: formData.project?.trim() || null,
        assigned_to: formData.assignTo || null,
      });

      if (!customer) {
        console.error("Error saving end customer");
        // Don't block the scheduling if customer save fails
      }

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Create scheduled date/time
      const [hours, minutes] = formData.time.split(":").map(Number);
      const scheduledDateTime = new Date(formData.date);
      scheduledDateTime.setHours(hours, minutes, 0, 0);

      const endDateTime = new Date(scheduledDateTime);
      endDateTime.setHours(endDateTime.getHours() + 1);

      // Generate room and join link for reminders
      const roomId = `scheduled_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      console.log('[ScheduleCallModal] Generated roomId:', {
        submissionId,
        roomId,
        timestamp: new Date().toISOString()
      });

      // CRITICAL: Guests must use /join/ to see phone verification in prejoin lobby
      const joinLink = `${getShareableBaseUrl().replace(/\/$/, "")}/join/${roomId}`;

      console.log('[ScheduleCallModal] Join link created:', {
        submissionId,
        joinLink,
      });

      // Normalize timezone to a valid IANA value; fallback to browser tz
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const eventTimezone =
        formData.timezone && formData.timezone.includes("/")
          ? formData.timezone
          : browserTz;

      // SECURITY: Use backend API instead of direct database insert
      const calendarResponse = await apiClient.post<{ ok: boolean; event: any }>('/calendar/events', {
        title: `Video Call with ${customerName}`,
        description: `Scheduled video call with ${customerName}`,
        start_time: scheduledDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        event_type: "scheduled-call",
        customer_name: customerName,
        customer_phone: fullPhoneNumber,
        customer_email: formData.shareOnEmail
          ? formData.email.trim() || null
          : null,
        assigned_to_user_id: formData.assignTo || null,
        user_id: user.id,
        timezone: eventTimezone || null,
        room_id: roomId,
        join_link: joinLink,
        call_link: joinLink,
      });

      if (!calendarResponse.ok) {
        throw new Error('Failed to create calendar event');
      }

      const calendarEvent = calendarResponse.event;

      console.log('[ScheduleCallModal] Calendar event created:', {
        submissionId,
        eventId: calendarEvent?.id,
        roomId: calendarEvent?.room_id,
        joinLink: calendarEvent?.join_link,
        customerName: calendarEvent?.customer_name
      });

      // 🔗 Create call session in signaling backend
      const BASE_URL =
        import.meta.env.VITE_SIGNALING_SERVER_URL ?? "http://localhost:3000";

      let callCode = roomId; // Default to roomId if no callCode

      try {
        const callSessionResponse = await fetch(`${BASE_URL}/api/create-call`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            roomId,
            hostUserId: user.id,
            customerData: {
              name: customerName,
              phone: fullPhoneNumber,
              email: formData.shareOnEmail
                ? formData.email.trim() || null
                : null,
            },
            callType: "scheduled",
            scheduledEventId: calendarEvent?.id ?? null,
          }),
        });

        const sessionData = await callSessionResponse.json();

        if (sessionData?.ok) {
          console.log(
            "✅ Scheduled call session created:",
            sessionData.session?.id
          );
          console.log("🔢 Call code:", sessionData.callCode);
          callCode = sessionData.callCode || roomId; // Use callCode from backend
        } else {
          console.warn(
            "Scheduled call session creation returned non-ok:",
            sessionData
          );
        }
      } catch (err) {
        console.error(
          "Failed to create call session for scheduled call via backend:",
          err
        );
        // Don't block scheduling if backend session creation fails
      }

      // 📨 Assignment notification is now handled by the backend in scheduled-calls.controller.ts
      // The backend sends the notification when assigned_to is set on the calendar event

      // Notify calendar to refresh immediately
      window.dispatchEvent(new Event("calendar:refresh"));

      // Send SMS notification using the send-sms edge function
      // NOTE: We don't fail the entire scheduling if SMS fails - call is already scheduled!
      // CRITICAL: Guests must use /join/ to see phone verification in prejoin lobby
      const callLink = `${getShareableBaseUrl()}/join/${roomId}`;

      console.log('[ScheduleCallModal] SMS link generation:', {
        submissionId,
        roomId,
        callLink,
        originalJoinLink: joinLink
      });

      const messageText = `${getFinalMessage()}

${callLink}`;

      console.log("Sending scheduled call SMS to:", fullPhoneNumber);
      console.log("Message:", messageText);

      let smsSuccess = false;
      let smsErrorMessage = "";

      try {
        // SECURITY: Use backend proxy instead of calling edge function directly
        const data = await apiClient.post<{ success: boolean; error?: string }>('/edge/send-sms', {
          to: fullPhoneNumber,
          message: messageText,
          type: "scheduled-call",
        });

        if (!data.success) {
          console.error("SMS sending failed:", data);
          smsErrorMessage = data.error || "Failed to send SMS";
        } else {
          console.log("SMS sent successfully:", data);
          smsSuccess = true;
        }
      } catch (smsErr: any) {
        console.error("SMS exception:", smsErr);
        smsErrorMessage = smsErr.message || "SMS delivery failed";
      }

      // Show appropriate success/warning message
      if (smsSuccess) {
        toast({
          title: t("notifications.success"),
          description: `Call scheduled and SMS sent to ${customerName}`,
        });
      } else {
        // Call was scheduled but SMS failed - show warning
        toast({
          title: "Call Scheduled",
          description: `Call scheduled successfully, but SMS notification failed: ${smsErrorMessage}. Please notify ${customerName} manually.`,
          variant: "default", // Changed from destructive since call was actually scheduled
        });
      }

      onOpenChange(false);

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        countryCode: "+1",
        email: "",
        message: "",
        shareOnEmail: false,
        assignTo: "",
        date: undefined,
        time: getCurrentTimeOption(),
        timezone: userTimezone,
      });
    } catch (error) {
      console.error("Error scheduling call:", error);

      // Extract meaningful error message
      let errorMessage = "Failed to schedule call";

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Handle Supabase/API errors with nested error objects
        const err = error as any;
        if (err.message) errorMessage = err.message;
        else if (err.error) errorMessage = err.error;
        else if (err.details) errorMessage = err.details;
      }

      toast({
        title: "Error Scheduling Call",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    onOpenChange(false);
  };

  // Clear form when modal is closed
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        countryCode: "+1",
        email: "",
        message: "",
        shareOnEmail: false,
        assignTo: "",
        date: undefined,
        time: getCurrentTimeOption(),
        timezone: userTimezone,
      });
      setIsDatePickerOpen(false);
    }
    onOpenChange(open);
  };

  // Generate 24-hour time options (every 15 minutes)
  const timeOptions: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString(
        [],
        {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }
      );
      timeOptions.push({ value: timeString, label: displayTime });
    }
  }

  // Fetch user role from team members
  useEffect(() => {
    if (user?.id && teamMembers.length > 0) {
      const currentMember = teamMembers.find(m => m.id === user.id);
      if (currentMember) {
        setUserRole(currentMember.role as 'admin' | 'user');
      }
    }
  }, [user, teamMembers]);

  // Set default assignTo to current user when teamMembers loads
  useEffect(() => {
    if (teamMembers.length > 0 && !formData.assignTo) {
      supabase.auth.getUser().then(({ data }) => {
        if (data.user) {
          setFormData((prev) => ({ ...prev, assignTo: data.user.id }));
        }
      });
    }
  }, [teamMembers, formData.assignTo]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px] max-h-[80vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950 dark:to-green-900">
          <DialogTitle className="flex items-center justify-between text-lg">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Video className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              {t("modals.scheduleCall.title")}
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
          {/* Date and Time Selection - Side by Side */}
          <div className="grid grid-cols-2 gap-3">
            {/* Date Selection */}
            <div className="space-y-1">
              <Label className="text-xs font-medium">
                {t("modals.scheduleCall.selectDate")}
              </Label>
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal text-xs",
                      !formData.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? (
                      format(formData.date, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background border shadow-lg z-50" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => {
                      setFormData((prev) => ({ ...prev, date: date || undefined }));
                      if (date) {
                        setIsDatePickerOpen(false);
                      }
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today || date < new Date("1900-01-01");
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Selection */}
            <div className="space-y-1">
              <Label className="text-xs font-medium">
                {t("modals.scheduleCall.selectTime")}
              </Label>
              <Select
                value={formData.time}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, time: value }))
                }
              >
                <SelectTrigger className="w-full text-xs">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent className="bg-background border shadow-lg z-50 max-h-60">
                  {timeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* First Name with autocomplete */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">First Name *</Label>
            <CustomerInput
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(value) => setFormData((prev) => ({ ...prev, firstName: value }))}
              onCustomerSelect={(customer) => {
                // Extract phone number: always use +1 and take last 10 digits
                const digitsOnly = customer.phone_number?.replace(/\D/g, '') || "";
                const last10Digits = digitsOnly.slice(-10);

                setFormData((prev) => ({
                  ...prev,
                  firstName: customer.first_name || "",
                  lastName: customer.last_name || "",
                  phoneNumber: last10Digits,
                  countryCode: "+1",
                  email: customer.email || "",
                  shareOnEmail: !!customer.email, // Auto-enable if email exists
                }));
              }}
              className="text-xs"
              required
              field="name"
            />
          </div>

          {/* Last Name */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Last Name</Label>
            <Input
              placeholder="Enter last name (optional)"
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
              }
              className="text-xs"
            />
          </div>

          {/* Phone Number */}
          <div className="space-y-1">
            <Label htmlFor="phone" className="text-xs font-medium">
              Phone Number
            </Label>
            <div className="flex gap-2">
              <div className="w-24">
                <Select
                  value={formData.countryCode}
                  onValueChange={(code) =>
                    setFormData((prev) => ({ ...prev, countryCode: code }))
                  }
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-md z-50">
                    <SelectItem value="+1">🇺🇸 +1</SelectItem>
                    <SelectItem value="+44">🇬🇧 +44</SelectItem>
                    <SelectItem value="+33">🇫🇷 +33</SelectItem>
                    <SelectItem value="+49">🇩🇪 +49</SelectItem>
                    <SelectItem value="+91">🇮🇳 +91</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <CustomerInput
                  placeholder="Phone number"
                  value={formData.phoneNumber.replace(/^\+\d+\s/, "")}
                  onChange={(value) => {
                    // Only allow digits and limit to 10
                    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
                    setFormData((prev) => ({ ...prev, phoneNumber: digitsOnly }));
                  }}
                  onCustomerSelect={(customer) => {
                    // Extract phone number: always use +1 and take last 10 digits
                    const digitsOnly = customer.phone_number?.replace(/\D/g, '') || "";
                    const last10Digits = digitsOnly.slice(-10);

                    setFormData((prev) => ({
                      ...prev,
                      firstName: customer.first_name || prev.firstName,
                      lastName: customer.last_name || prev.lastName,
                      phoneNumber: last10Digits,
                      countryCode: "+1",
                      email: customer.email || prev.email,
                      shareOnEmail: !!customer.email || prev.shareOnEmail, // Auto-enable if email exists
                    }));
                  }}
                  className="text-xs"
                  required
                  field="phone_number"
                />
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Message</Label>
            <Textarea
              placeholder="Edit your message here..."
              value={formData.message || getMessagePreview()}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, message: e.target.value }))
              }
              className="text-xs min-h-[80px]"
              autoCorrect="on"
              spellCheck="true"
            />
          </div>

          {/* Email (optional) */}
          <div className="flex items-center justify-between">
            <Label htmlFor="share-email" className="text-xs font-medium">
              Email (Optional)
            </Label>
            <Switch
              id="share-email"
              checked={formData.shareOnEmail}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, shareOnEmail: checked }))
              }
            />
          </div>

          {/* Email field - shown when shareOnEmail is true */}
          {formData.shareOnEmail && (
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder=""
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                className="text-xs"
                required={formData.shareOnEmail}
              />
            </div>
          )}

          {/* Assign to User (Admin only) */}
          {userRole === 'admin' && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Assign to User</Label>
              <Select
                value={formData.assignTo || undefined}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, assignTo: value }))
                }
                disabled={loadingTeam || !teamMembers.length}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loadingTeam ? "Loading team members..." : teamMembers.length ? "Select a user" : "No users available"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.length ? (
                    teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="unavailable" disabled>
                      No users available
                    </SelectItem>
                )}
              </SelectContent>
            </Select>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button type="submit" className="px-8" disabled={isLoading}>
              {isLoading ? "Scheduling..." : "Schedule Call"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
